import React, { useState, useMemo, useEffect } from 'react';
import { X, DollarSign, Calendar, FileText, Plus, Trash2, CheckCircle, AlertCircle, Loader2, RefreshCw, ExternalLink } from 'lucide-react';
import Button from '../../UI/Button';

const CombinedPaymentLinkModal = ({
    isOpen,
    onClose,
    onSubmit,
    isLoading,
    clientName,
    availableFees,
    loadingFees,
    onRegenerateLink,
    onDeleteInvoice,
    regeneratingInvoice,
    deletingInvoice
}) => {
    const [selectedFees, setSelectedFees] = useState([]);
    const [customItems, setCustomItems] = useState([]);
    const [dueDate, setDueDate] = useState('');
    const [description, setDescription] = useState('');
    const [errors, setErrors] = useState({});
    const [enteredAmounts, setEnteredAmounts] = useState({}); // Track amounts entered for fees without amounts

    // Auto-select uninvoiced fees when modal opens
    useEffect(() => {
        if (availableFees && isOpen) {
            const clientProgressFees = availableFees.client_progress_fees || [];
            const priorReturnsFees = availableFees.prior_returns_fees || [];
            const allFees = [...clientProgressFees, ...priorReturnsFees];

            // Auto-select fees that are uninvoiced (have amount but not invoiced)
            const uninvoicedFees = allFees.filter(fee => fee.status === 'uninvoiced');
            setSelectedFees(uninvoicedFees);
        }
    }, [availableFees, isOpen]);

    // Helper to get unique fee key
    const getFeeKey = (fee) => `${fee.source}|${fee.source_id}|${fee.fee_field || ''}`;

    // Calculate total amount (using entered amounts for fees without amounts)
    const totalAmount = useMemo(() => {
        const feeTotal = selectedFees.reduce((sum, fee) => {
            const key = getFeeKey(fee);
            // Use entered amount if fee has no amount, otherwise use DB amount
            const amount = fee.has_amount === false
                ? parseFloat(enteredAmounts[key] || 0)
                : parseFloat(fee.amount || 0);
            return sum + amount;
        }, 0);
        const customTotal = customItems.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
        return feeTotal + customTotal;
    }, [selectedFees, customItems, enteredAmounts]);

    // Check if a fee is selected
    const isFeeSelected = (fee) => {
        return selectedFees.some(
            f => f.source === fee.source &&
                 f.source_id === fee.source_id &&
                 f.fee_field === fee.fee_field
        );
    };

    // Get unselected fees for the dropdown (all fees not yet selected), sorted by amount
    const unselectedFees = useMemo(() => {
        const clientProgressFees = availableFees?.client_progress_fees || [];
        const priorReturnsFees = availableFees?.prior_returns_fees || [];
        const allFees = [...clientProgressFees, ...priorReturnsFees];

        return allFees
            .filter(fee =>
                !selectedFees.some(
                    f => f.source === fee.source &&
                         f.source_id === fee.source_id &&
                         f.fee_field === fee.fee_field
                )
            )
            .sort((a, b) => (b.amount || 0) - (a.amount || 0));
    }, [availableFees, selectedFees]);

    // Toggle fee selection
    const handleToggleFee = (fee) => {
        if (isFeeSelected(fee)) {
            setSelectedFees(prev => prev.filter(
                f => !(f.source === fee.source &&
                      f.source_id === fee.source_id &&
                      f.fee_field === fee.fee_field)
            ));
            // Also clear entered amount when deselecting
            if (fee.has_amount === false) {
                const key = getFeeKey(fee);
                setEnteredAmounts(prev => {
                    const updated = { ...prev };
                    delete updated[key];
                    return updated;
                });
            }
        } else {
            setSelectedFees(prev => [...prev, fee]);
        }

        // Clear error when user selects
        if (errors.selection) {
            setErrors(prev => ({ ...prev, selection: '' }));
        }
    };

    // Handle entering amount for fees without amounts
    const handleFeeAmountChange = (fee, amount) => {
        const key = getFeeKey(fee);
        setEnteredAmounts(prev => ({
            ...prev,
            [key]: amount
        }));
    };

    // Quick add fee from dropdown
    const handleQuickAddFee = (feeKey) => {
        if (!feeKey) return;

        // Parse the key to find the fee
        const [source, sourceId, feeField] = feeKey.split('|');
        const clientProgressFees = availableFees?.client_progress_fees || [];
        const priorReturnsFees = availableFees?.prior_returns_fees || [];
        const allFees = [...clientProgressFees, ...priorReturnsFees];

        const fee = allFees.find(f =>
            f.source === source &&
            String(f.source_id) === sourceId &&
            (f.fee_field || '') === (feeField || '')
        );

        if (fee) {
            setSelectedFees(prev => [...prev, fee]);

            // Clear error when user selects
            if (errors.selection) {
                setErrors(prev => ({ ...prev, selection: '' }));
            }
        }
    };

    // Add custom line item
    const handleAddCustomItem = () => {
        setCustomItems(prev => [...prev, { label: '', amount: '' }]);
    };

    // Update custom line item
    const handleUpdateCustomItem = (index, field, value) => {
        setCustomItems(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });

        // Clear custom item errors
        if (errors.customItems) {
            setErrors(prev => ({ ...prev, customItems: '' }));
        }
    };

    // Remove custom line item
    const handleRemoveCustomItem = (index) => {
        setCustomItems(prev => prev.filter((_, i) => i !== index));
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};

        // Must have at least one fee or custom item selected
        const hasValidCustomItems = customItems.some(item => item.label.trim() && parseFloat(item.amount) > 0);
        if (selectedFees.length === 0 && !hasValidCustomItems) {
            newErrors.selection = 'Please select at least one fee or add a custom item';
        }

        // Check that all selected fees without amounts have entered amounts
        const feesWithoutAmounts = selectedFees.filter(fee => fee.has_amount === false);
        const missingAmounts = feesWithoutAmounts.some(fee => {
            const key = getFeeKey(fee);
            const amount = parseFloat(enteredAmounts[key] || 0);
            return amount <= 0;
        });
        if (missingAmounts) {
            newErrors.feeAmounts = 'Please enter amounts for all selected fees';
        }

        // Validate custom items
        const invalidCustomItems = customItems.some(item => {
            if (item.label.trim() || item.amount) {
                return !item.label.trim() || !item.amount || parseFloat(item.amount) <= 0;
            }
            return false;
        });
        if (invalidCustomItems) {
            newErrors.customItems = 'Custom items must have a description and positive amount';
        }

        // Validate due date
        if (!dueDate) {
            newErrors.dueDate = 'Due date is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle form submission
    const handleSubmit = (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        // Build fee items array (using entered amounts for fees without amounts)
        const feeItems = [
            ...selectedFees.map(fee => {
                const key = getFeeKey(fee);
                // Use entered amount if fee has no amount, otherwise use DB amount
                const amount = fee.has_amount === false
                    ? parseFloat(enteredAmounts[key] || 0)
                    : parseFloat(fee.amount || 0);
                return {
                    source: fee.source,
                    source_id: fee.source_id,
                    fee_field: fee.fee_field,
                    amount: amount,
                    label: fee.label,
                    is_custom: false
                };
            }),
            ...customItems
                .filter(item => item.label.trim() && parseFloat(item.amount) > 0)
                .map(item => ({
                    source: 'custom',
                    source_id: null,
                    fee_field: null,
                    amount: parseFloat(item.amount),
                    label: item.label.trim(),
                    is_custom: true
                }))
        ];

        onSubmit({
            dueDate,
            description: description.trim() || null,
            selectedFees: feeItems
        });
    };

    // Handle modal close
    const handleClose = () => {
        if (!isLoading) {
            setSelectedFees([]);
            setCustomItems([]);
            setDueDate('');
            setDescription('');
            setErrors({});
            setEnteredAmounts({});
            onClose();
        }
    };

    // Get status badge
    const getStatusBadge = (status, hasAmount) => {
        // Show "No amount" badge for fees without amounts
        if (hasAmount === false) {
            return (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                    No amount set
                </span>
            );
        }

        switch (status) {
            case 'paid':
                return (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                        <CheckCircle size={12} className="mr-1" />
                        Paid
                    </span>
                );
            case 'invoiced_unpaid':
                return (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200">
                        <AlertCircle size={12} className="mr-1" />
                        Invoiced
                    </span>
                );
            default:
                return null;
        }
    };

    if (!isOpen) return null;

    // Sort fees by amount (highest first)
    const clientProgressFees = (availableFees?.client_progress_fees || [])
        .slice()
        .sort((a, b) => (b.amount || 0) - (a.amount || 0));
    const priorReturnsFees = (availableFees?.prior_returns_fees || [])
        .slice()
        .sort((a, b) => (b.amount || 0) - (a.amount || 0));
    const hasUninvoicedFees = [...clientProgressFees, ...priorReturnsFees].some(f => f.status === 'uninvoiced');

    return (
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
                onClick={handleClose}
            />

            {/* Modal */}
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                <div
                    className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl mx-auto max-h-[90vh] flex flex-col"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                            Create Combined Invoice
                        </h2>
                        <button
                            onClick={handleClose}
                            disabled={isLoading}
                            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                        >
                            <X size={24} />
                        </button>
                    </div>

                    {/* Scrollable Content */}
                    <div className="p-6 overflow-y-auto flex-1">
                        {/* Client Name Display */}
                        <div className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                            Creating invoice for: <span className="font-medium text-gray-900 dark:text-white">{clientName}</span>
                        </div>

                        {loadingFees ? (
                            <div className="flex items-center justify-center py-12">
                                <Loader2 className="animate-spin text-blue-500" size={32} />
                                <span className="ml-3 text-gray-600 dark:text-gray-400">Loading fees...</span>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-6">
                                {/* Selection Error */}
                                {errors.selection && (
                                    <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                                        <p className="text-red-600 dark:text-red-400 text-sm">{errors.selection}</p>
                                    </div>
                                )}

                                {/* Fee Amounts Error */}
                                {errors.feeAmounts && (
                                    <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                                        <p className="text-red-600 dark:text-red-400 text-sm">{errors.feeAmounts}</p>
                                    </div>
                                )}

                                {/* Client Progress Fees Section */}
                                {clientProgressFees.length > 0 && (
                                    <div>
                                        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                                            Current Year Fees
                                        </h3>
                                        <div className="space-y-2">
                                            {clientProgressFees.map((fee, index) => {
                                                const feeKey = getFeeKey(fee);
                                                const isSelected = isFeeSelected(fee);
                                                const needsAmount = fee.has_amount === false;
                                                const enteredAmount = enteredAmounts[feeKey] || '';

                                                return (
                                                    <div
                                                        key={`cp-${index}`}
                                                        className={`p-3 rounded-lg border transition-colors ${
                                                            isSelected
                                                                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                                                                : 'border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-700'
                                                        }`}
                                                    >
                                                        <div
                                                            className="flex items-center justify-between cursor-pointer"
                                                            onClick={() => handleToggleFee(fee)}
                                                        >
                                                            <div className="flex items-center">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isSelected}
                                                                    onChange={() => handleToggleFee(fee)}
                                                                    disabled={isLoading}
                                                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
                                                                    onClick={(e) => e.stopPropagation()}
                                                                />
                                                                <span className="ml-3 mr-2 text-sm font-medium text-gray-700 dark:text-gray-200">
                                                                    {fee.label}
                                                                </span>
                                                                {getStatusBadge(fee.status, fee.has_amount)}
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                {fee.has_amount !== false && (
                                                                    <span className="text-sm font-semibold text-gray-900 dark:text-white">
                                                                        ${parseFloat(fee.amount).toFixed(2)}
                                                                    </span>
                                                                )}
                                                                {/* Action buttons for invoiced fees */}
                                                                {fee.status === 'invoiced_unpaid' && fee.invoice_id && (
                                                                    <div className="flex gap-1 ml-2" onClick={(e) => e.stopPropagation()}>
                                                                        {fee.checkout_url && (
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => window.open(fee.checkout_url, '_blank')}
                                                                                className="p-1 text-green-500 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 rounded hover:bg-green-100 dark:hover:bg-green-900/30"
                                                                                title="View Payment Link"
                                                                            >
                                                                                <ExternalLink size={14} />
                                                                            </button>
                                                                        )}
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => onRegenerateLink && onRegenerateLink(fee)}
                                                                            disabled={regeneratingInvoice === fee.invoice_id}
                                                                            className="p-1 text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 rounded hover:bg-blue-100 dark:hover:bg-blue-900/30"
                                                                            title="Regenerate Payment Link"
                                                                        >
                                                                            <RefreshCw size={14} className={regeneratingInvoice === fee.invoice_id ? 'animate-spin' : ''} />
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => onDeleteInvoice && onDeleteInvoice(fee)}
                                                                            disabled={deletingInvoice === fee.invoice_id}
                                                                            className="p-1 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 rounded hover:bg-red-100 dark:hover:bg-red-900/30"
                                                                            title="Delete Invoice"
                                                                        >
                                                                            <Trash2 size={14} />
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                        {/* Amount input for fees without amounts */}
                                                        {isSelected && needsAmount && (
                                                            <div className="mt-3 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                                                <label className="text-sm text-gray-600 dark:text-gray-400">Enter amount:</label>
                                                                <div className="relative">
                                                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                                                                    <input
                                                                        type="number"
                                                                        placeholder="0.00"
                                                                        step="0.01"
                                                                        min="0"
                                                                        value={enteredAmount}
                                                                        onChange={(e) => handleFeeAmountChange(fee, e.target.value)}
                                                                        disabled={isLoading}
                                                                        className="w-32 pl-7 pr-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                                    />
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* Prior Returns Fees Section */}
                                {priorReturnsFees.length > 0 && (
                                    <div>
                                        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                                            Prior Year Returns
                                        </h3>
                                        <div className="space-y-2">
                                            {priorReturnsFees.map((fee, index) => {
                                                const isSelected = isFeeSelected(fee);
                                                return (
                                                    <div
                                                        key={`rn-${index}`}
                                                        className={`flex items-center justify-between p-3 rounded-lg border transition-colors cursor-pointer ${
                                                            isSelected
                                                                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                                                                : 'border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-700'
                                                        }`}
                                                        onClick={() => handleToggleFee(fee)}
                                                    >
                                                        <div className="flex items-center">
                                                            <input
                                                                type="checkbox"
                                                                checked={isSelected}
                                                                onChange={() => handleToggleFee(fee)}
                                                                disabled={isLoading}
                                                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
                                                                onClick={(e) => e.stopPropagation()}
                                                            />
                                                            <span className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-200">
                                                                {fee.label}
                                                            </span>
                                                            {getStatusBadge(fee.status, fee.has_amount)}
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-sm font-semibold text-gray-900 dark:text-white">
                                                                ${parseFloat(fee.amount).toFixed(2)}
                                                            </span>
                                                            {/* Action buttons for invoiced fees */}
                                                            {fee.status === 'invoiced_unpaid' && fee.invoice_id && (
                                                                <div className="flex gap-1 ml-2" onClick={(e) => e.stopPropagation()}>
                                                                    {fee.checkout_url && (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => window.open(fee.checkout_url, '_blank')}
                                                                            className="p-1 text-green-500 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 rounded hover:bg-green-100 dark:hover:bg-green-900/30"
                                                                            title="View Payment Link"
                                                                        >
                                                                            <ExternalLink size={14} />
                                                                        </button>
                                                                    )}
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => onRegenerateLink && onRegenerateLink(fee)}
                                                                        disabled={regeneratingInvoice === fee.invoice_id}
                                                                        className="p-1 text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 rounded hover:bg-blue-100 dark:hover:bg-blue-900/30"
                                                                        title="Regenerate Payment Link"
                                                                    >
                                                                        <RefreshCw size={14} className={regeneratingInvoice === fee.invoice_id ? 'animate-spin' : ''} />
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => onDeleteInvoice && onDeleteInvoice(fee)}
                                                                        disabled={deletingInvoice === fee.invoice_id}
                                                                        className="p-1 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 rounded hover:bg-red-100 dark:hover:bg-red-900/30"
                                                                        title="Delete Invoice"
                                                                    >
                                                                        <Trash2 size={14} />
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* No Fees Message */}
                                {clientProgressFees.length === 0 && priorReturnsFees.length === 0 && (
                                    <div className="text-center py-6 text-gray-500 dark:text-gray-400">
                                        No fees found for this client.
                                    </div>
                                )}

                                {/* Quick Add Fee Dropdown */}
                                {unselectedFees.length > 0 && (
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                            Quick Add Fee
                                        </label>
                                        <select
                                            onChange={(e) => handleQuickAddFee(e.target.value)}
                                            value=""
                                            disabled={isLoading}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        >
                                            <option value="">Select a fee to add...</option>
                                            {unselectedFees.map((fee, idx) => (
                                                <option
                                                    key={`${fee.source}-${fee.source_id}-${fee.fee_field || ''}-${idx}`}
                                                    value={`${fee.source}|${fee.source_id}|${fee.fee_field || ''}`}
                                                >
                                                    {fee.label} {fee.has_amount === false ? '(no amount set)' : `- $${parseFloat(fee.amount).toFixed(2)}`}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                {/* Custom Line Items Section */}
                                <div>
                                    <div className="flex items-center justify-between mb-3">
                                        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                            Custom Line Items
                                        </h3>
                                        <button
                                            type="button"
                                            onClick={handleAddCustomItem}
                                            disabled={isLoading}
                                            className="inline-flex items-center text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                                        >
                                            <Plus size={16} className="mr-1" />
                                            Add Item
                                        </button>
                                    </div>

                                    {errors.customItems && (
                                        <p className="text-red-500 text-sm mb-2">{errors.customItems}</p>
                                    )}

                                    {customItems.length > 0 ? (
                                        <div className="space-y-2">
                                            {customItems.map((item, index) => (
                                                <div key={index} className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                                    <input
                                                        type="text"
                                                        placeholder="Description"
                                                        value={item.label}
                                                        onChange={(e) => handleUpdateCustomItem(index, 'label', e.target.value)}
                                                        disabled={isLoading}
                                                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    />
                                                    <div className="relative">
                                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                                                        <input
                                                            type="number"
                                                            placeholder="0.00"
                                                            step="0.01"
                                                            min="0"
                                                            value={item.amount}
                                                            onChange={(e) => handleUpdateCustomItem(index, 'amount', e.target.value)}
                                                            disabled={isLoading}
                                                            className="w-28 pl-7 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                        />
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveCustomItem(index)}
                                                        disabled={isLoading}
                                                        className="p-2 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                                                    >
                                                        <Trash2 size={18} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                                            No custom items added
                                        </p>
                                    )}
                                </div>

                                {/* Total Amount */}
                                <div className="p-4 bg-gray-100 dark:bg-gray-700 rounded-lg">
                                    <div className="flex items-center justify-between">
                                        <span className="text-lg font-semibold text-gray-700 dark:text-gray-300">
                                            Total Amount
                                        </span>
                                        <span className="text-2xl font-bold text-gray-900 dark:text-white">
                                            ${totalAmount.toFixed(2)}
                                        </span>
                                    </div>
                                    {selectedFees.length > 0 && (
                                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                            {selectedFees.length} fee{selectedFees.length !== 1 ? 's' : ''} selected
                                            {customItems.filter(i => i.label && i.amount).length > 0 &&
                                                ` + ${customItems.filter(i => i.label && i.amount).length} custom item${customItems.filter(i => i.label && i.amount).length !== 1 ? 's' : ''}`
                                            }
                                        </p>
                                    )}
                                </div>

                                {/* Due Date */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        <Calendar size={16} className="inline mr-2" />
                                        Due Date
                                    </label>
                                    <input
                                        type="date"
                                        value={dueDate}
                                        onChange={(e) => {
                                            setDueDate(e.target.value);
                                            if (errors.dueDate) setErrors(prev => ({ ...prev, dueDate: '' }));
                                        }}
                                        className={`w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                            errors.dueDate ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                                        }`}
                                        disabled={isLoading}
                                    />
                                    {errors.dueDate && (
                                        <p className="text-red-500 text-sm mt-1">{errors.dueDate}</p>
                                    )}
                                </div>

                                {/* Description (Optional) */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        <FileText size={16} className="inline mr-2" />
                                        Description (Optional)
                                    </label>
                                    <textarea
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        placeholder="Enter invoice description..."
                                        rows={2}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                                        disabled={isLoading}
                                    />
                                </div>
                            </form>
                        )}
                    </div>

                    {/* Footer - Action Buttons */}
                    <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={handleClose}
                            disabled={isLoading}
                            className="px-4 py-2"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            onClick={handleSubmit}
                            disabled={isLoading || loadingFees || totalAmount <= 0}
                            className="px-4 py-2"
                        >
                            {isLoading ? 'Creating...' : `Create Invoice ($${totalAmount.toFixed(2)})`}
                        </Button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default CombinedPaymentLinkModal;
